import { Preferences } from '@capacitor/preferences';
import type { z } from 'zod';

/**
 * Small settings kept on the device (native key-value storage on Android, localStorage in the
 * browser). Reads are validated, so a stored value from an older version falls back cleanly.
 */
export async function loadPreference<T>(
  key: string,
  schema: z.ZodType<T>,
  fallback: T,
): Promise<T> {
  try {
    const { value } = await Preferences.get({ key });
    if (value === null) return fallback;
    const parsed = schema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : fallback;
  } catch {
    return fallback;
  }
}

export async function savePreference<T>(key: string, value: T): Promise<void> {
  try {
    await Preferences.set({ key, value: JSON.stringify(value) });
  } catch {
    // Storage can be unavailable (private browsing); the setting then lasts for this visit.
  }
}

export async function removePreference(key: string): Promise<void> {
  try {
    await Preferences.remove({ key });
  } catch {
    // Nothing to remove.
  }
}
